import React, { useEffect, useState } from 'react';
import { Button, Modal, Spin, message } from 'antd';
import {
  USER_STATUS_LABEL,
  queryAdminUserDetail,
  type AdminUserDetail,
  type QuotaDetail
} from './userManageData';

type UserDetailModalProps = {
  open: boolean;
  userCode: string | null;
  onClose: () => void;
};

function dash(value?: string | null) {
  return value && value.trim() ? value : '—';
}

function yesNo(value: boolean) {
  return value ? '是' : '否';
}

function statusText(status: string) {
  if (status === 'NORMAL' || status === 'EXPIRING' || status === 'DISABLED') {
    return USER_STATUS_LABEL[status];
  }
  return status || '—';
}

function quotaText(quota: QuotaDetail, unlimited = false) {
  if (unlimited) return '不限';
  return `${quota.used}/${quota.total}`;
}

function Cell({ label, value, full }: { label: string; value: React.ReactNode; full?: boolean }) {
  return (
    <div className={`admin-order-detail-cell${full ? ' admin-order-detail-cell--full' : ''}`}>
      <span className='admin-order-detail-label'>{label}</span>
      <span className='admin-order-detail-value'>{value}</span>
    </div>
  );
}

export function UserDetailModal({ open, userCode, onClose }: UserDetailModalProps) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);

  useEffect(() => {
    if (!open || !userCode) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await queryAdminUserDetail(userCode);
        if (cancelled) return;
        if (res.code !== 200 || !res.detail) {
          message.error(res.message || '用户详情加载失败');
          setDetail(null);
          return;
        }
        setDetail(res.detail);
      } catch {
        if (!cancelled) message.error('用户详情加载失败，请稍后重试');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, userCode]);

  return (
    <Modal
      title='用户详情'
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={860}
      className='admin-order-detail-modal admin-user-detail-modal'
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      footer={
        <div className='flex justify-end'>
          <Button onClick={onClose}>关闭</Button>
        </div>
      }
    >
      {loading ? (
        <div className='flex justify-center py-16'>
          <Spin />
        </div>
      ) : detail ? (
        <div className='admin-order-detail-body'>
          <section className='admin-order-detail-card'>
            <div className='admin-order-detail-card-title'>账号</div>
            <div className='admin-order-detail-grid'>
              <Cell label='用户编码' value={dash(detail.userCode)} />
              <Cell label='用户名' value={dash(detail.displayName)} />
              <Cell label='手机号' value={dash(detail.phone)} />
              <Cell label='邮箱' value={dash(detail.email)} />
              <Cell label='地区' value={dash(detail.regionCode)} />
              <Cell label='语言' value={dash(detail.locale)} />
              <Cell label='公众号' value={yesNo(detail.wechatOfficialAccountBound)} />
              <Cell label='小程序' value={yesNo(detail.wechatMiniProgramBound)} />
              <Cell label='状态' value={statusText(detail.status)} />
              <Cell label='可启用' value={yesNo(detail.canEnable)} />
              <Cell label='注册时间' value={dash(detail.registeredAt)} />
              <Cell label='停用时间' value={dash(detail.disabledAt)} />
            </div>
          </section>

          <section className='admin-order-detail-card'>
            <div className='admin-order-detail-card-title'>会员</div>
            <div className='admin-order-detail-grid'>
              <Cell label='套餐' value={dash(detail.membershipName)} />
              <Cell label='等级' value={dash(detail.membershipGrade)} />
              <Cell label='到期时间' value={dash(detail.membershipExpiresAt)} />
            </div>
          </section>

          <section className='admin-order-detail-card'>
            <div className='admin-order-detail-card-title'>额度</div>
            <div className='admin-order-detail-grid'>
              <Cell label='店铺已用' value={quotaText(detail.shop)} />
              <Cell label='店铺剩余' value={String(detail.shop.remaining)} />
              <Cell label='套餐店铺' value={String(detail.shop.planTotal)} />
              <Cell label='加购店铺' value={String(detail.shop.addonTotal)} />
              <Cell label='预占店铺' value={String(detail.shop.reserved)} />
              <Cell label='超出上限' value={yesNo(detail.shop.overLimit)} />
              <Cell label='邮件已用' value={quotaText(detail.mail, detail.mail.unlimited)} />
              <Cell label='邮件剩余' value={detail.mail.unlimited ? '不限' : String(detail.mail.remaining)} />
              <Cell label='套餐邮件' value={String(detail.mail.planTotal)} />
              <Cell label='加购邮件' value={String(detail.mail.addonTotal)} />
              <Cell label='预占邮件' value={String(detail.mail.reserved)} />
              <Cell label='下次重置' value={dash(detail.mail.nextResetAt)} />
            </div>
          </section>

          <section className='admin-order-detail-card'>
            <div className='admin-order-detail-card-title'>冻结套餐</div>
            {detail.frozenMemberships.length === 0 ? (
              <p className='admin-user-detail-empty'>暂无冻结套餐</p>
            ) : (
              detail.frozenMemberships.map((item) => (
                <div key={`${item.planCode}-${item.frozenAt}`} className='admin-order-detail-grid'>
                  <Cell label='套餐编码' value={dash(item.planCode)} />
                  <Cell label='名称' value={dash(item.name)} />
                  <Cell label='等级' value={dash(item.grade)} />
                  <Cell label='剩余时长' value={dash(item.remaining)} />
                  <Cell label='冻结时间' value={dash(item.frozenAt)} full />
                </div>
              ))
            )}
          </section>

          <section className='admin-order-detail-card'>
            <div className='admin-order-detail-card-title'>权益包</div>
            {detail.entitlementPackages.length === 0 ? (
              <p className='admin-user-detail-empty'>暂无权益包</p>
            ) : (
              detail.entitlementPackages.map((item) => (
                <div key={item.orderNo || item.name} className='admin-user-detail-package'>
                  <div className='admin-order-detail-grid'>
                    <Cell label='订单号' value={dash(item.orderNo)} />
                    <Cell label='名称' value={dash(item.name)} />
                    <Cell label='开始时间' value={dash(item.startsAt)} />
                    <Cell label='到期时间' value={dash(item.expiresAt)} />
                  </div>
                  {item.features.length > 0 ? (
                    <ul className='admin-user-detail-features'>
                      {item.features.map((feature, index) => (
                        <li key={`${feature.title}-${index}`}>
                          <span className='admin-user-detail-feature-title'>{dash(feature.title)}</span>
                          <span>{feature.supported ? '支持' : '不支持'}</span>
                          {feature.quota ? <span>{feature.quota}{feature.unit}</span> : null}
                          {feature.period ? <span>{feature.period}</span> : null}
                          {feature.text ? <span>{feature.text}</span> : null}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))
            )}
          </section>
        </div>
      ) : (
        <p className='admin-user-detail-empty'>暂无用户详情</p>
      )}
    </Modal>
  );
}
